import React, { useState } from "react";
import {
  AlertTriangle,
  Check,
  Eye,
  ChevronDown,
  ChevronUp,
  Info,
  Shield,
  Zap,
  ArrowRight,
  ExternalLink,
} from "lucide-react";

// Summary Card Component
const SummaryCard = ({
  icon: Icon,
  count,
  label,
  sublabel,
  color,
  gradient,
}) => (
  <div
    className={`relative overflow-hidden rounded-2xl p-5 ${gradient} border border-${color}/20`}
  >
    <div className="flex items-center justify-between">
      <div
        className={`w-12 h-12 rounded-xl bg-${color}/20 flex items-center justify-center`}
      >
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
      <span className={`text-4xl font-bold text-${color}`}>{count}</span>
    </div>
    <p className={`text-sm font-semibold text-${color} mt-3`}>{label}</p>
    <p className="text-xs text-neutral-dark mt-1">{sublabel}</p>
  </div>
);

// Conflict Card Component
const ConflictCard = ({
  conflict,
  currentRule,
  isExpanded,
  onToggle,
  index,
}) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case "high":
        return {
          color: "danger",
          gradient: "from-danger/5 to-danger/10",
          border: "border-danger/30",
          icon: AlertTriangle,
          label: "High Priority",
        };
      case "medium":
        return {
          color: "warning",
          gradient: "from-warning/5 to-warning/10",
          border: "border-warning/30",
          icon: Info,
          label: "Medium Priority",
        };
      default:
        return {
          color: "primary",
          gradient: "from-primary/5 to-primary/10",
          border: "border-primary/30",
          icon: Eye,
          label: "Low Priority",
        };
    }
  };

  const config = getSeverityConfig(conflict.severity);
  const SeverityIcon = config.icon;

  return (
    <div
      className={`bg-white rounded-2xl border-2 ${config.border} overflow-hidden transition-all duration-300 hover:shadow-lg`}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className={`w-full p-5 text-left transition-colors hover:bg-gradient-to-r ${config.gradient}`}
      >
        <div className="flex items-center gap-4">
          {/* Severity Icon */}
          <div
            className={`w-12 h-12 rounded-xl bg-${config.color}/10 flex items-center justify-center flex-shrink-0`}
          >
            <SeverityIcon className={`w-6 h-6 text-${config.color}`} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-neutral-dark truncate">
                {conflict.rule.name}
              </h4>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${config.color}/10 text-${config.color}`}
              >
                {config.label}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral">
              <span>
                {conflict.issues.length} issue
                {conflict.issues.length > 1 ? "s" : ""} detected
              </span>
              <span>•</span>
              <span>Priority: {conflict.rule.priority}</span>
              <span>•</span>
              <span
                className={
                  conflict.rule.isActive ? "text-success" : "text-neutral"
                }
              >
                {conflict.rule.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          {/* Expand Icon */}
          <div
            className={`w-10 h-10 rounded-xl bg-neutral-lightest flex items-center justify-center transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          >
            <ChevronDown className="w-5 h-5 text-neutral" />
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div
          className={`p-5 border-t ${config.border} bg-gradient-to-b ${config.gradient}`}
        >
          {/* Issues List */}
          <div className="mb-5">
            <h5 className="text-sm font-semibold text-neutral-dark mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Detected Issues
            </h5>
            <div className="space-y-2">
              {conflict.issues.map((issue, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-white rounded-xl border border-neutral-light/50"
                >
                  <div
                    className={`w-6 h-6 rounded-full bg-${config.color}/10 flex items-center justify-center flex-shrink-0 mt-0.5`}
                  >
                    <span className={`text-xs font-bold text-${config.color}`}>
                      {idx + 1}
                    </span>
                  </div>
                  <span className="text-sm text-neutral-dark">{issue}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rule Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            {/* Current Rule */}
            <div className="bg-white rounded-xl p-4 border border-neutral-light/50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <h6 className="text-sm font-semibold text-neutral-dark">
                  Current Rule
                </h6>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral">Priority</span>
                  <span className="font-semibold text-neutral-dark">
                    {currentRule.priority}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral">Products</span>
                  <span className="font-medium text-neutral-dark truncate ml-4">
                    {currentRule.conditions?.productTypes?.join(", ") || "All"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral">Status</span>
                  <span
                    className={`font-semibold ${
                      currentRule.isActive ? "text-success" : "text-danger"
                    }`}
                  >
                    {currentRule.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Conflicting Rule */}
            <div className="bg-white rounded-xl p-4 border border-neutral-light/50">
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={`w-8 h-8 rounded-lg bg-${config.color}/10 flex items-center justify-center`}
                >
                  <Zap className={`w-4 h-4 text-${config.color}`} />
                </div>
                <h6 className="text-sm font-semibold text-neutral-dark">
                  Conflicting Rule
                </h6>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral">Priority</span>
                  <span className="font-semibold text-neutral-dark">
                    {conflict.rule.priority}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral">Products</span>
                  <span className="font-medium text-neutral-dark truncate ml-4">
                    {conflict.rule.conditions?.productTypes?.join(", ") ||
                      "All"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral">Status</span>
                  <span
                    className={`font-semibold ${
                      conflict.rule.isActive ? "text-success" : "text-danger"
                    }`}
                  >
                    {conflict.rule.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Resolution Suggestions */}
          <div
            className={`bg-${config.color}/5 border border-${config.color}/20 rounded-xl p-4 mb-5`}
          >
            <h6
              className={`text-sm font-semibold text-${config.color} mb-3 flex items-center gap-2`}
            >
              💡 Resolution Suggestions
            </h6>
            <ul className="space-y-2 text-sm text-neutral-dark">
              {conflict.severity === "high" && (
                <>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Adjust priority levels to ensure proper rule execution
                      order
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Narrow down product type or weight range criteria
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Consider deactivating one of the conflicting rules
                    </span>
                  </li>
                </>
              )}
              {conflict.severity === "medium" && (
                <>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Review and adjust weight ranges if applicable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Consider different priority levels to establish precedence
                    </span>
                  </li>
                </>
              )}
              {conflict.severity === "low" && (
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Monitor both rules to ensure expected behavior in production
                  </span>
                </li>
              )}
            </ul>
          </div>

          {/* Actions */}
          <button
            onClick={() =>
              window.open(`/pricing-rules/${conflict.rule._id}`, "_blank")
            }
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-neutral-light text-neutral-dark rounded-xl font-medium hover:bg-neutral-lightest transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>View Conflicting Rule</span>
            <ExternalLink className="w-3 h-3 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

const ConflictDetection = ({ currentRule, allRules }) => {
  const [expandedConflict, setExpandedConflict] = useState(null);

  // Detect conflicts
  const detectConflicts = () => {
    const conflicts = [];

    allRules.forEach((rule) => {
      if (rule._id === currentRule._id) return;

      const conflict = {
        rule,
        issues: [],
        severity: "low",
      };

      // Check product type overlap
      const hasProductOverlap = currentRule.conditions?.productTypes?.some(
        (type) => rule.conditions?.productTypes?.includes(type)
      );

      // Check purity overlap
      const hasPurityOverlap = currentRule.conditions?.purityLevels?.some(
        (purity) => rule.conditions?.purityLevels?.includes(purity)
      );

      // Check branch overlap
      const hasBranchOverlap =
        !currentRule.conditions?.branchCodes?.length ||
        !rule.conditions?.branchCodes?.length ||
        currentRule.conditions?.branchCodes?.some((branch) =>
          rule.conditions?.branchCodes?.includes(branch)
        );

      // Check weight range overlap
      const hasWeightOverlap = !(
        (currentRule.conditions?.maxWeight &&
          rule.conditions?.minWeight &&
          currentRule.conditions.maxWeight < rule.conditions.minWeight) ||
        (rule.conditions?.maxWeight &&
          currentRule.conditions?.minWeight &&
          rule.conditions.maxWeight < currentRule.conditions.minWeight)
      );

      if (hasProductOverlap && hasPurityOverlap && hasBranchOverlap) {
        conflict.issues.push("Product type and purity overlap");

        if (hasWeightOverlap) {
          conflict.issues.push("Weight range overlap");
          conflict.severity = "high";
        } else {
          conflict.severity = "medium";
        }

        // Check priority conflict
        if (rule.priority === currentRule.priority) {
          conflict.issues.push(
            "Same priority level - may cause unpredictable behavior"
          );
          conflict.severity = "high";
        } else if (Math.abs(rule.priority - currentRule.priority) <= 2) {
          conflict.issues.push("Similar priority levels - close precedence");
        }

        // Check validity period overlap
        const currentStart = new Date(currentRule.validFrom);
        const currentEnd = currentRule.validUntil
          ? new Date(currentRule.validUntil)
          : null;
        const ruleStart = new Date(rule.validFrom);
        const ruleEnd = rule.validUntil ? new Date(rule.validUntil) : null;

        const hasDateOverlap =
          (!currentEnd || !ruleEnd || currentStart <= ruleEnd) &&
          (!ruleEnd || !currentEnd || ruleStart <= currentEnd);

        if (hasDateOverlap) {
          conflict.issues.push("Validity period overlap");
        }

        conflicts.push(conflict);
      }
    });

    return conflicts.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  };

  const conflicts = detectConflicts();
  const highCount = conflicts.filter((c) => c.severity === "high").length;
  const mediumCount = conflicts.filter((c) => c.severity === "medium").length;
  const lowCount = conflicts.filter((c) => c.severity === "low").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl lg:text-2xl font-bold text-neutral-dark flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-warning" />
          Conflict Detection
        </h2>
        <p className="text-neutral mt-1">
          Rules that may conflict with "{currentRule.name}"
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          icon={AlertTriangle}
          count={highCount}
          label="High Priority"
          sublabel="Requires immediate attention"
          color="danger"
          gradient="bg-gradient-to-br from-danger/5 to-danger/10"
        />
        <SummaryCard
          icon={Info}
          count={mediumCount}
          label="Medium Priority"
          sublabel="Review recommended"
          color="warning"
          gradient="bg-gradient-to-br from-warning/5 to-warning/10"
        />
        <SummaryCard
          icon={Eye}
          count={lowCount}
          label="Low Priority"
          sublabel="For information only"
          color="primary"
          gradient="bg-gradient-to-br from-primary/5 to-primary/10"
        />
      </div>

      {/* No Conflicts State */}
      {conflicts.length === 0 ? (
        <div className="bg-gradient-to-br from-success/5 to-success/10 border-2 border-success/20 rounded-2xl p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-success/20 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-success" />
          </div>
          <h3 className="text-xl font-bold text-success-dark mb-2">
            No Conflicts Detected
          </h3>
          <p className="text-neutral-dark max-w-md mx-auto">
            This rule doesn't conflict with any existing rules. You're all set!
          </p>
        </div>
      ) : (
        /* Conflicts List */
        <div className="space-y-4">
          {conflicts.map((conflict, index) => (
            <ConflictCard
              key={conflict.rule._id}
              conflict={conflict}
              currentRule={currentRule}
              isExpanded={expandedConflict === index}
              onToggle={() =>
                setExpandedConflict(expandedConflict === index ? null : index)
              }
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConflictDetection;
